/**
 * Visual objects for everything that moves: the player jet, pooled enemies, the bridge and its guns, enemy shots,
 * player tracers and the explosion light (GDD §3.6–3.7). Built once at load and posed from a FrameState
 * (GDD §10.5: no object creation in the game loop; §10.3: pooling and merging).
 */
import { CylinderGeometry, Mesh, PointLight, Sprite, Vector3, type Group, type Scene } from 'three';
import { mulberry32 } from '../core/rng.ts';
import type { EntityType, FrameState } from '../render/frame.ts';
import type { WorldProfile } from '../world/profiles.ts';
import { createMaterials, type TextureMaps } from './materials.ts';
import { buildBarge, type BargeParts } from './models/barge.ts';
import { bake } from './models/bake.ts';
import { buildBridge, type BridgeParts } from './models/bridge.ts';
import { buildGunboat, type GunboatParts } from './models/gunboat.ts';
import { buildHeli, type HeliParts } from './models/heli.ts';
import { buildJet, type JetParts } from './models/jet.ts';
import { buildTurret, type TurretParts } from './models/turret.ts';
import type { Look } from '../world/looks.ts';
import { POOL_SIZES } from './pool.ts';
import { SHOT_POOL } from '../sim/gameSim.ts';
import type { GameSim } from '../sim/context.ts';
import type { ExplosionLight } from '../effects/explosions.ts';
import { lerp } from '../core/math.ts';

export { POOL_SIZES, SHOT_POOL };
/** Player tracers: 0.9 s life at 0.09 s twin volleys needs 20 live; 24 keeps the full 310 m range (D14). */
export const TRACER_POOL = 24;

type Model<P> = Group & { userData: P };

export interface EntityViews {
  jet: Model<JetParts>;
  pools: {
    ship: Model<GunboatParts>[];
    heli: Model<HeliParts>[];
    fuel: Model<BargeParts>[];
    jet: Model<JetParts>[];
  };
  bridge: Model<BridgeParts>;
  turrets: Model<TurretParts>[];
  fxLight: PointLight;
  shots: Mesh[];
  tracers: Mesh[];
  /** Poses every model from a recorded frame (the M1 parity path). */
  apply(frame: FrameState): void;
  /**
   * Poses every model straight from the game simulation, `alpha` of the way from the previous step (live game).
   * Allocation-free. The player jet's pose is the caller's; its visibility (blinking) comes from the run.
   */
  poseSim(sim: GameSim, alpha: number, light: ExplosionLight): void;
}

export function createEntityViews(scene: Scene, W: WorldProfile, look: Look, TX: TextureMaps): EntityViews {
  const { M, M2, labelMat, wakeMat } = createMaterials(TX, look);

  // Player jet (concept L778–L779).
  const jet = buildJet(M);
  scene.add(jet);
  jet.traverse((o) => {
    if (o instanceof Mesh && o.material !== M.flameOuter && o.material !== M.flameInner) o.castShadow = true;
  });

  // Player tracers (concept L815–L816).
  const tracerGeo = new CylinderGeometry(0.13, 0.13, 6, 6, 1, true);
  tracerGeo.rotateX(Math.PI / 2);
  const tracers = Array.from({ length: TRACER_POOL }, () => {
    const t = new Mesh(tracerGeo, M.tracer);
    t.visible = false;
    scene.add(t);
    return t;
  });

  // Bridge with two gun turrets; trucks from its own seed (concept L887–L890).
  const span = 2 * (W.width(W.bridgeZ) + 44);
  const bridge = buildBridge(span, W.deckY, M, mulberry32(19));
  bridge.position.set(W.center(W.bridgeZ), 0, W.bridgeZ);
  scene.add(bridge);
  const turrets = [1, bridge.userData.n - 2].map((i) => {
    const tu = buildTurret(M);
    tu.position.set(0, 0.1, 3.4);
    bridge.userData.segs[i]?.add(tu);
    return tu;
  });

  // Explosion flash on the surroundings (GDD §3.7; concept L891).
  const fxLight = new PointLight(0xff8a3a, 0, 0, 2);
  scene.add(fxLight);

  // Enemy shots with muzzle glow (concept L896–L897).
  const shotGeo = new CylinderGeometry(0.22, 0.22, 4.5, 6, 1, true);
  shotGeo.rotateX(Math.PI / 2);
  const shots = Array.from({ length: SHOT_POOL }, () => {
    const m = new Mesh(shotGeo, M.etracer);
    m.visible = false;
    const glow = new Sprite(M.muzzle);
    glow.scale.set(2.2, 2.2, 1);
    m.add(glow);
    scene.add(m);
    return m;
  });

  // Enemy models, baked exactly as the concept's makeModel (concept L899–L908), pre-built into pools.
  const hidden = <T extends Group>(m: T): T => {
    m.visible = false;
    scene.add(m);
    return m;
  };
  const pools: EntityViews['pools'] = {
    ship: Array.from({ length: POOL_SIZES.ship }, () => {
      const m = buildGunboat(M, wakeMat);
      return hidden(bake(m, [m.userData.radar], M.baked));
    }),
    heli: Array.from({ length: POOL_SIZES.heli }, () => {
      const m = buildHeli(M);
      return hidden(bake(m, [m.userData.rotor, m.userData.tail, m.userData.beacon], M.baked));
    }),
    fuel: Array.from({ length: POOL_SIZES.fuel }, () => {
      const m = buildBarge(M, labelMat);
      return hidden(bake(m, [...m.userData.beacons, m.userData.label], M.baked));
    }),
    jet: Array.from({ length: POOL_SIZES.jet }, () => {
      const m = buildJet(M2);
      bake(m, [m.userData.strobe], M.baked);
      m.scale.setScalar(0.78); // GDD §3.6: 78% of the player jet
      return hidden(m);
    }),
  };

  function apply(frame: FrameState): void {
    // Player jet.
    jet.visible = frame.jet.visible;
    jet.position.fromArray(frame.jet.pos);
    jet.quaternion.fromArray(frame.jet.quat);
    frame.jet.flameScaleZ.forEach((z, i) => jet.userData.flames[i]?.scale.set(1, 1, z));
    jet.userData.glow.scale.setScalar(frame.jet.glowScale);
    jet.userData.strobe.visible = frame.jet.strobe;

    // Enemies: the n-th entity of a type takes the n-th pooled model.
    const used: Record<EntityType, number> = { ship: 0, heli: 0, fuel: 0, jet: 0 };
    for (const e of frame.ents) {
      const pool = pools[e.type];
      const m = pool[used[e.type]++];
      if (!m) throw new Error(`entity pool '${e.type}' exhausted (${pool.length}); raise POOL_SIZES`);
      m.visible = e.visible;
      m.position.fromArray(e.pos);
      m.quaternion.fromArray(e.quat);
      const a = e.anim;
      if (e.type === 'heli') {
        const u = m.userData as HeliParts;
        u.rotor.rotation.y = a.rotor ?? 0;
        u.tail.rotation.x = a.tail ?? 0;
        u.beacon.visible = a.beacon ?? false;
      } else if (e.type === 'ship') {
        const u = m.userData as GunboatParts;
        u.radar.rotation.y = a.radar ?? 0;
        u.wake.visible = u.bow.visible = a.wake ?? false;
      } else if (e.type === 'fuel') {
        for (const b of (m.userData as BargeParts).beacons) b.visible = a.beacon ?? false;
      } else {
        (m.userData as JetParts).strobe.visible = a.beacon ?? false;
      }
    }
    for (const type of Object.keys(pools) as EntityType[])
      pools[type].slice(used[type]).forEach((m) => (m.visible = false));

    // Bridge: spans fall and sag independently; turrets track the jet.
    const br = frame.bridge;
    bridge.visible = br !== null;
    if (br) {
      const [rx, ry, rz] = br.root;
      bridge.position.set(rx, ry, rz);
      br.segs.forEach((s, i) => {
        const sg = bridge.userData.segs[i];
        if (!sg) return;
        sg.visible = s.visible;
        // Spans are children of the unrotated bridge root: local = world − root.
        sg.position.set(s.pos[0] - rx, s.pos[1] - ry, s.pos[2] - rz);
        sg.quaternion.fromArray(s.quat);
      });
      br.turrets.forEach((t, i) => {
        const tu = turrets[i];
        if (!tu) return;
        tu.visible = t.visible;
        tu.userData.head.rotation.y = t.headY;
        tu.userData.flash.visible = t.flash;
      });
    }

    fxLight.intensity = frame.fx?.intensity ?? 0;
    if (frame.fx) fxLight.position.fromArray(frame.fx.pos);

    shots.forEach((m, i) => {
      const s = frame.shots[i];
      m.visible = !!s;
      if (!s) return;
      m.position.fromArray(s.pos);
      m.quaternion.fromArray(s.quat);
      m.scale.fromArray(s.scale);
    });
    tracers.forEach((m, i) => {
      const t = frame.tracers[i];
      m.visible = !!t;
      if (!t) return;
      m.position.fromArray(t.pos);
      m.quaternion.fromArray(t.quat);
    });
  }

  // Scratch for the live path (no allocation per frame).
  const used: Record<EntityType, Uint8Array> = {
    ship: new Uint8Array(POOL_SIZES.ship),
    heli: new Uint8Array(POOL_SIZES.heli),
    fuel: new Uint8Array(POOL_SIZES.fuel),
    jet: new Uint8Array(POOL_SIZES.jet),
  };
  const back = new Vector3(0, 0, -1);
  const dir = new Vector3();

  function hideUnused(list: Group[], flags: Uint8Array): void {
    for (let i = 0; i < list.length; i++) {
      const m = list[i];
      if (m && !flags[i]) m.visible = false;
      flags[i] = 0;
    }
  }

  function poseSim(sim: GameSim, alpha: number, light: ExplosionLight): void {
    const a = alpha < 0 ? 0 : alpha > 1 ? 1 : alpha;
    jet.visible = sim.run.jetVisible;

    // Enemies: each live enemy draws with its slot's model (concept: the model it acquired).
    const ents = sim.ents;
    for (let i = 0; i < ents.count; i++) {
      const e = ents.items[i];
      if (!e) continue;
      const m = pools[e.type][e.slot];
      if (!m) continue;
      used[e.type][e.slot] = 1;
      m.visible = true;
      if (e.posed) m.position.set(lerp(e.prevX, e.posX, a), lerp(e.prevY, e.posY, a), lerp(e.prevZ, e.posZ, a));
      else m.position.set(e.posX, e.posY, e.posZ);
      if (e.type === 'heli') {
        m.rotation.set(e.rotX, e.rotY, e.rotZ, 'YXZ');
        const u = m.userData as HeliParts;
        u.rotor.rotation.y = sim.pool.rotor[e.slot] ?? 0;
        u.tail.rotation.x = sim.pool.tail[e.slot] ?? 0;
        u.beacon.visible = e.light;
      } else {
        m.rotation.set(e.rotX, e.rotY, e.rotZ, 'XYZ');
        if (e.type === 'ship') {
          const u = m.userData as GunboatParts;
          u.radar.rotation.y = e.radar;
          u.wake.visible = u.bow.visible = e.wake;
        } else if (e.type === 'fuel') {
          const bs = (m.userData as BargeParts).beacons;
          for (let k = 0; k < bs.length; k++) (bs[k] as Mesh).visible = e.light;
        } else (m.userData as JetParts).strobe.visible = e.light;
      }
    }
    hideUnused(pools.ship, used.ship);
    hideUnused(pools.heli, used.heli);
    hideUnused(pools.fuel, used.fuel);
    hideUnused(pools.jet, used.jet);

    // Bridge: spans in the root's frame; the guns track the jet.
    const b = sim.bridge;
    bridge.visible = true;
    bridge.position.set(b.rootX, 0, b.z);
    const segs = bridge.userData.segs;
    for (let i = 0; i < b.segs.length; i++) {
      const s = b.segs[i];
      const sg = segs[i];
      if (!s || !sg) continue;
      sg.visible = s.visible;
      sg.position.set(s.x, s.y, s.z);
      sg.rotation.set(s.rotX, s.rotY, s.rotZ);
    }
    for (let i = 0; i < b.turrets.length; i++) {
      const t = b.turrets[i];
      const tu = turrets[i];
      if (!t || !tu) continue;
      tu.visible = t.visible;
      tu.userData.head.rotation.y = t.headY;
      tu.userData.flash.visible = t.flash;
    }

    fxLight.intensity = light.intensity;
    fxLight.position.set(light.x, light.y, light.z);

    // Enemy shots: each in its own slot's mesh, pointing along its flight; flak shells squashed (concept L991–L992).
    for (let i = 0; i < shots.length; i++) {
      const m = shots[i];
      if (m) m.visible = sim.shotUsed[i] === 1;
    }
    for (let i = 0; i < sim.shots.count; i++) {
      const s = sim.shots.items[i];
      const m = s ? shots[s.slot] : undefined;
      if (!s || !m) continue;
      m.position.set(lerp(s.prevX, s.x, a), lerp(s.prevY, s.y, a), lerp(s.prevZ, s.z, a));
      m.quaternion.setFromUnitVectors(back, dir.set(s.vx, s.vy, s.vz).normalize());
      if (s.kind === 'flak') m.scale.set(1.6, 1.6, 0.35);
      else m.scale.set(1, 1, 1);
    }

    // Player tracers (concept L818–L821).
    for (let i = 0; i < tracers.length; i++) {
      const m = tracers[i];
      const t = sim.tracers[i];
      if (!m) continue;
      m.visible = !!t && t.visible;
      if (!t || !t.visible) continue;
      m.position.set(lerp(t.prevX, t.x, a), lerp(t.prevY, t.y, a), lerp(t.prevZ, t.z, a));
      m.quaternion.setFromUnitVectors(back, dir.set(t.vx, t.vy, t.vz).normalize());
    }
  }

  return { jet, pools, bridge, turrets, fxLight, shots, tracers, apply, poseSim };
}
